/**
 * Share a Guardian answer with the department (WS-F, workspace docs/guardian/06,
 * 2026-10-23). Opened from an assistant answer in the chat. It posts a text
 * snapshot of the answer plus an optional note to the department board as
 * `kind:'answer'` with the turn's `recordId` (the Backend verifies the record is
 * the caller's own turn). The snapshot text is bounded to 8000 chars and the
 * note to 1000; the answer renders as plain text here, never as markdown/HTML.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { postDepartmentBoardPost } from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';

export const MAX_ANSWER_TEXT = 8000;
export const MAX_NOTE = 1000;

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string|null} props.authToken
 * @param {{recordId?: string, content?: string}|null} props.answer
 * @param {() => void} props.onClose
 * @param {() => void} [props.onShared]
 */
export default function ShareToDepartmentDialog({ open, authToken, answer, onClose, onShared }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setNote('');
      setError(null);
      setBusy(false);
      setDone(false);
    }
  }, [open, answer]);

  const recordId = answer?.recordId || null;
  const text = typeof answer?.content === 'string' ? answer.content.slice(0, MAX_ANSWER_TEXT) : '';
  const canShare = Boolean(recordId && text.trim());

  const submit = async () => {
    if (!canShare) return;
    setBusy(true);
    setError(null);
    const res = await postDepartmentBoardPost(authToken, {
      kind: 'answer',
      text,
      note: note.trim() || undefined,
      recordId,
    });
    setBusy(false);
    if (res?.success) {
      setDone(true);
      onShared?.();
      return;
    }
    setError(departmentErrorMessage(res, 'Could not share this answer.'));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share to department</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {done ? (
          <Alert severity="success">Shared with your department board.</Alert>
        ) : (
          <>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {!canShare && (
              <Alert severity="info">This answer can&apos;t be shared.</Alert>
            )}
            <Typography variant="caption" color="text.secondary">Answer preview</Typography>
            <Box sx={{ maxHeight: 180, overflow: 'auto', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>
            </Box>
            <TextField
              size="small"
              label="Add a note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: MAX_NOTE, 'aria-label': 'Note' }}
              multiline
              minRows={2}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button variant="contained" onClick={submit} disabled={busy || !canShare}>
            {busy ? 'Sharing…' : 'Share'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
